import { AuditRecordDetail } from './AuditRecordDetail';

interface AuditRecordRowProps {
  record: {
    id: string;
    sequenceNumber: string;
    eventType: string;
    eventCategory: string;
    actorType?: string;
    actorId?: string | null;
    actorEmail?: string | null;
    resourceType?: string | null;
    resourceId?: string | null;
    affectedEntities?: Array<{ type: string; id: string; label?: string }> | null;
    details?: Record<string, unknown> | null;
    ipAddress?: string | null;
    userAgent?: string | null;
    conversationId?: string | null;
    conversationTitle?: string | null;
    parentAuditRecordId?: string | null;
    legalHold?: boolean;
    createdAt: string | Date;
  };
  isExpanded: boolean;
  onToggle: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  conversation: 'Chat',
  system_configuration: 'System Config',
  task_agent: 'Task Agent',
  security_event: 'Security',
  data_access: 'Data Access',
};

function formatEventType(eventType: string): string {
  // 'security.login_failed' -> 'Login Failed'
  const parts = eventType.split('.');
  const action = parts[parts.length - 1] ?? eventType;
  return action
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatTimestamp(dateStr: string | Date): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  return d.toISOString().replace('T', ' ').replace('Z', '').slice(0, 23);
}

function formatRelativeTime(dateStr: string | Date): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatTimestamp(dateStr);
}

export function AuditRecordRow({ record, isExpanded, onToggle }: AuditRecordRowProps) {
  const categoryLabel = CATEGORY_LABELS[record.eventCategory] ?? record.eventCategory;
  const resourceSummary = record.resourceType
    ? `${record.resourceType}${record.resourceId ? `/${record.resourceId}` : ''}`
    : '\u2014';

  return (
    <div className={`audit-row ${isExpanded ? 'audit-row-expanded' : ''}`}>
      <div className="audit-row-summary" onClick={onToggle}>
        <span className="audit-col-seq"><code>{record.sequenceNumber}</code></span>
        <span className="audit-col-time" title={formatTimestamp(record.createdAt)}>
          {formatRelativeTime(record.createdAt)}
        </span>
        <span className="audit-col-category">
          <span className={`audit-badge audit-badge-${record.eventCategory}`}>{categoryLabel}</span>
        </span>
        <span className="audit-col-event">{formatEventType(record.eventType)}</span>
        <span className="audit-col-chat" title={record.conversationId ?? undefined}>
          {record.conversationId ? (
            <>
              <span className="audit-chat-title">{record.conversationTitle || 'Untitled chat'}</span>
              <code className="audit-chat-id">{record.conversationId}</code>
            </>
          ) : '\u2014'}
        </span>
        <span className="audit-col-actor">{record.actorEmail ?? record.actorId ?? '\u2014'}</span>
        <span className="audit-col-resource">{resourceSummary}</span>
        <span className="audit-col-actions">
          {record.legalHold && <span className="audit-legal-hold-badge">Hold</span>}
          <span className="audit-expand-icon">{isExpanded ? '\u25BE' : '\u25B8'}</span>
        </span>
      </div>
      {isExpanded && <AuditRecordDetail record={record} />}
    </div>
  );
}
